import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, Users, X, Upload, Search, GripVertical } from 'lucide-react';
import { teamService, mediaService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

function TeamPage() {
  const [team, setTeam] = useState([]);
  const [filteredTeam, setFilteredTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    bio: '',
    image_url: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    loadTeam();
  }, []);

  useEffect(() => {
    filterTeam();
  }, [team, searchQuery, filterStatus]);

  const filterTeam = () => {
    let filtered = [...team];

    // Recherche par nom ou poste
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(member =>
        member.name.toLowerCase().includes(query) ||
        member.position.toLowerCase().includes(query)
      );
    }

    // Filtre par statut
    if (filterStatus !== 'all') {
      filtered = filtered.filter(member =>
        filterStatus === 'active' ? member.is_active : !member.is_active
      );
    }

    setFilteredTeam(filtered);
  };

  const loadTeam = async () => {
    try {
      const response = await teamService.getAll();
      console.log('Team chargée:', response.data);
      setTeam(response.data.team || []);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (member = null) => {
    if (member) {
      setEditing(member);
      setFormData({
        name: member.name || '',
        position: member.position || '',
        bio: member.bio || '',
        image_url: member.image_url || '',
        display_order: member.display_order || 0,
        is_active: member.is_active ? true : false
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        position: '',
        bio: '',
        image_url: '',
        display_order: team.length + 1,
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploading(true);
    try {
      const response = await mediaService.uploadImage(file);
      const BASE_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5000';
      const imageUrl = `${BASE_URL}${response.data.media.url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.position) {
      toast.error('Nom et poste sont requis');
      return;
    }

    try {
      if (editing) {
        await teamService.update(editing.id, formData);
        toast.success('Membre mis à jour');
      } else {
        await teamService.create(formData);
        toast.success('Membre créé');
      }
      setShowModal(false);
      loadTeam();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce membre de l\'équipe ?')) return;

    try {
      await teamService.delete(id);
      toast.success('Membre supprimé');
      loadTeam();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (member) => {
    try {
      await teamService.update(member.id, { is_active: !member.is_active });
      toast.success('Statut mis à jour');
      loadTeam();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Équipe</h1>
          <p className="text-dark-500">Gérez les membres de votre équipe</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Nouveau membre
        </button>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card p-6 mb-6">
        <div className="grid md:grid-cols-3 gap-4">
          {/* Recherche */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par nom ou poste..."
                className="input pl-10"
              />
            </div>
          </div>

          {/* Filtre Statut */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
        </div>

        {/* Résumé */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-dark-100">
          <div className="flex items-center gap-4">
            {(searchQuery || filterStatus !== 'all') && (
              <button
                onClick={resetFilters}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
          <div className="text-sm text-dark-500">
            {filteredTeam.length} membre{filteredTeam.length > 1 ? 's' : ''} affiché{filteredTeam.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Liste des membres */}
      {filteredTeam.length === 0 ? (
        <div className="card p-12 text-center">
          <Users className="w-16 h-16 text-dark-300 mx-auto mb-4" />
          <p className="text-dark-500 mb-4">
            {team.length === 0
              ? 'Aucun membre dans l\'équipe'
              : 'Aucun résultat pour ces critères'}
          </p>
          {team.length === 0 ? (
            <button onClick={() => openModal()} className="btn-primary">
              Ajouter un membre
            </button>
          ) : (
            <button onClick={resetFilters} className="btn-ghost">
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTeam.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card p-6 relative group"
            >
              {/* Actions */}
              <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => openModal(member)}
                  className="p-2 bg-white rounded-lg shadow hover:bg-dark-50"
                  title="Modifier"
                >
                  <Edit className="w-4 h-4 text-dark-600" />
                </button>
                <button
                  onClick={() => handleDelete(member.id)}
                  className="p-2 bg-white rounded-lg shadow hover:bg-red-50"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>

              {/* Ordre d'affichage */}
              <div className="absolute top-4 left-4">
                <span className="flex items-center gap-1 text-xs text-dark-400 bg-dark-100 px-2 py-1 rounded">
                  <GripVertical className="w-3 h-3" />
                  #{member.display_order}
                </span>
              </div>

              {/* Photo / Initiale */}
              <div className="text-center mb-4 mt-6">
                {member.image_url ? (
                  <img
                    src={member.image_url}
                    alt={member.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto border-4 border-dark-100"
                  />
                ) : (
                  <div className="w-24 h-24 bg-[#1a3254] rounded-full flex items-center justify-center mx-auto">
                    <span className="text-3xl font-bold text-white">
                      {member.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              {/* Infos */}
              <div className="text-center">
                <h3 className="font-bold text-dark-900 text-lg">{member.name}</h3>
                <p className="text-primary-600 text-sm font-medium mb-2">{member.position}</p>
                <p className="text-dark-500 text-sm line-clamp-3">{member.bio}</p>
              </div>

              {/* Status */}
              <div className="mt-4 pt-4 border-t border-dark-100 flex items-center justify-center">
                <button
                  onClick={() => toggleActive(member)}
                  className={`badge cursor-pointer ${member.is_active ? 'badge-secondary' : 'bg-dark-100 text-dark-500'}`}
                >
                  {member.is_active ? 'Actif' : 'Inactif'}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-dark-900/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-dark-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold">
                {editing ? 'Modifier le membre' : 'Nouveau membre'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-dark-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Photo */}
              <div>
                <label className="label">Photo</label>
                <div className="flex items-center gap-4">
                  {formData.image_url ? (
                    <div className="relative">
                      <img
                        src={formData.image_url}
                        alt="Preview"
                        className="w-24 h-24 rounded-full object-cover border-4 border-dark-100"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                        className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="w-24 h-24 border-2 border-dashed border-dark-200 rounded-full flex items-center justify-center cursor-pointer hover:border-primary-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                      {uploading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Upload className="w-6 h-6 text-dark-400" />
                      )}
                    </label>
                  )}
                  <div className="text-sm text-dark-500">
                    <p>Photo du membre</p>
                    <p>JPG, PNG (max 5MB)</p>
                  </div>
                </div>
              </div>

              {/* Nom */}
              <div>
                <label className="label">Nom complet *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input"
                  required
                  placeholder="Ex: Fabrice BOUATE"
                />
              </div>

              {/* Poste */}
              <div>
                <label className="label">Poste / Fonction *</label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                  className="input"
                  required
                  placeholder="Ex: CEO & Fondateur"
                />
              </div>

              {/* Bio */}
              <div>
                <label className="label">Biographie</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  className="input"
                  rows={3}
                  placeholder="Courte description du membre..."
                />
              </div>

              {/* Ordre d'affichage */}
              <div>
                <label className="label">Ordre d'affichage</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  className="input"
                  min="0"
                />
                <p className="text-xs text-dark-400 mt-1">Les membres sont triés par ordre croissant</p>
              </div>

              {/* Actif */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="is_active" className="text-dark-700">
                  Membre actif (visible sur le site)
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-dark-100">
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editing ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default TeamPage;